/**
 * TanStack Query hooks — the single home for server state.
 * Zustand keeps only client state (auth session, toasts, theme).
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import api from '../utils/api';
import type {
  Analytics,
  ChallengeCard,
  DetailedResultResponse,
  Friend,
  FriendRequestsResponse,
  GameSummary,
  HeadToHead,
  LeaderboardResponse,
  LeaderboardTopic,
  NotificationsResponse,
  PublicProfile,
  QuizDetail,
  QuizSessionResult,
  QuizSessionStart,
  QuizSummary,
  TestResult,
  UserSearchHit,
} from '../types';

export const queryKeys = {
  analytics: (userId: string) => ['analytics', userId] as const,
  results: (userId: string) => ['results', userId] as const,
  resultDetail: (resultId: string) => ['result', resultId] as const,
  game: ['game'] as const,
  leaderboard: (topic: string, window: string) => ['leaderboard', topic, window] as const,
  leaderboardTopics: ['leaderboard-topics'] as const,
  myQuizzes: ['quizzes', 'mine'] as const,
  publishedQuizzes: ['quizzes', 'published'] as const,
  quiz: (id: string) => ['quiz', id] as const,
  friends: ['friends'] as const,
  friendRequests: ['friend-requests'] as const,
  userSearch: (q: string) => ['user-search', q] as const,
  challenges: ['challenges'] as const,
  challenge: (id: string) => ['challenges', id] as const,
  headToHead: (userId: string) => ['head-to-head', userId] as const,
  publicProfile: (userId: string) => ['public-profile', userId] as const,
  notifications: ['notifications'] as const,
};

export const useAnalytics = (userId: string | undefined) =>
  useQuery({
    queryKey: queryKeys.analytics(userId ?? ''),
    queryFn: async () => {
      const { data } = await api.get(`/api/results/analytics/${userId}`);
      return (data.analytics ?? data) as Analytics;
    },
    enabled: !!userId,
  });

export const useUserResults = (userId: string | undefined) =>
  useQuery({
    queryKey: queryKeys.results(userId ?? ''),
    queryFn: async () => {
      const { data } = await api.get(`/api/results/user/${userId}`);
      return (data.results ?? data) as TestResult[];
    },
    enabled: !!userId,
  });

export const useResultDetail = (resultId: string | undefined) =>
  useQuery({
    queryKey: queryKeys.resultDetail(resultId ?? ''),
    queryFn: async () => {
      const { data } = await api.get(`/api/results/${resultId}/detailed`);
      return data as DetailedResultResponse;
    },
    enabled: !!resultId,
  });

interface StartQuizInput {
  topic: string;
  difficulty: string;
  count: number;
}

export const useStartQuiz = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: StartQuizInput) => {
      const { data } = await api.post('/api/quiz/sessions', input);
      return data as QuizSessionStart;
    },
    // An AI generation spends 1 energy — keep the pill honest
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['wallet'] }),
  });
};

interface SubmitQuizInput {
  sessionId: string;
  answers: (string | null)[];
}

export const useSubmitQuiz = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ sessionId, answers }: SubmitQuizInput) => {
      const { data } = await api.post(`/api/quiz/sessions/${sessionId}/submit`, { answers });
      return data as QuizSessionResult;
    },
    onSuccess: () => {
      // A finished run changes history, aggregates, points and rankings
      queryClient.invalidateQueries({ queryKey: ['results'] });
      queryClient.invalidateQueries({ queryKey: ['analytics'] });
      queryClient.invalidateQueries({ queryKey: ['game'] });
      queryClient.invalidateQueries({ queryKey: ['leaderboard'] });
      // ... and may have settled a challenge
      queryClient.invalidateQueries({ queryKey: ['challenges'] });
      queryClient.invalidateQueries({ queryKey: ['friends'] });
    },
  });
};

// --- Game systems ---

export const useMyGame = () =>
  useQuery({
    queryKey: queryKeys.game,
    queryFn: async () => {
      const { data } = await api.get('/api/me/game');
      return data as GameSummary;
    },
  });

export const useLeaderboardTopics = () =>
  useQuery({
    queryKey: queryKeys.leaderboardTopics,
    queryFn: async () => {
      const { data } = await api.get('/api/leaderboards/topics');
      return (data.topics ?? []) as LeaderboardTopic[];
    },
  });

export const useLeaderboard = (topic: string, window: string) =>
  useQuery({
    queryKey: queryKeys.leaderboard(topic, window),
    queryFn: async () => {
      const params = new URLSearchParams({ window });
      if (topic) params.set('topic', topic);
      const { data } = await api.get(`/api/leaderboards?${params.toString()}`);
      return data as LeaderboardResponse;
    },
  });

export const useMyQuizzes = () =>
  useQuery({
    queryKey: queryKeys.myQuizzes,
    queryFn: async () => {
      const { data } = await api.get('/api/quizzes/mine');
      return (data.quizzes ?? []) as QuizSummary[];
    },
  });

export const usePublishedQuizzes = () =>
  useQuery({
    queryKey: queryKeys.publishedQuizzes,
    queryFn: async () => {
      const { data } = await api.get('/api/quizzes');
      return (data.quizzes ?? []) as QuizSummary[];
    },
  });

export const useQuiz = (id: string | undefined) =>
  useQuery({
    queryKey: queryKeys.quiz(id ?? ''),
    queryFn: async () => {
      const { data } = await api.get(`/api/quizzes/${id}`);
      return data.quiz as QuizDetail;
    },
    enabled: !!id,
  });

interface QuizInput {
  title: string;
  topic: string;
  difficulty: string;
  questions: { question: string; options: string[]; correctAnswer: string }[];
}

export const useSaveQuiz = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...body }: QuizInput & { id?: string }) => {
      const { data } = id
        ? await api.put(`/api/quizzes/${id}`, body)
        : await api.post('/api/quizzes', body);
      return data.quiz as QuizDetail;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['quizzes'] }),
  });
};

export const usePublishQuiz = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { data } = await api.post(`/api/quizzes/${id}/publish`);
      return data.quiz as QuizSummary;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['quizzes'] }),
  });
};

export const useDeleteQuiz = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/api/quizzes/${id}`);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['quizzes'] }),
  });
};

// --- Social (Phase F) ---

export const useFriends = () =>
  useQuery({
    queryKey: queryKeys.friends,
    queryFn: async () => {
      const { data } = await api.get('/api/friends');
      return (data.friends ?? []) as Friend[];
    },
  });

export const useFriendRequests = () =>
  useQuery({
    queryKey: queryKeys.friendRequests,
    queryFn: async () => {
      const { data } = await api.get('/api/friends/requests');
      return data as FriendRequestsResponse;
    },
  });

export const useUserSearch = (q: string) =>
  useQuery({
    queryKey: queryKeys.userSearch(q),
    queryFn: async () => {
      const { data } = await api.get(`/api/friends/search?q=${encodeURIComponent(q)}`);
      return (data.users ?? []) as UserSearchHit[];
    },
    enabled: q.trim().length >= 2,
  });

const invalidateFriendData = (queryClient: ReturnType<typeof useQueryClient>) => {
  queryClient.invalidateQueries({ queryKey: ['friends'] });
  queryClient.invalidateQueries({ queryKey: ['friend-requests'] });
  queryClient.invalidateQueries({ queryKey: ['user-search'] });
};

export const useSendFriendRequest = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (userId: string) => {
      const { data } = await api.post('/api/friends/requests', { user_id: userId });
      return data;
    },
    onSuccess: () => invalidateFriendData(queryClient),
  });
};

export const useRespondFriendRequest = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ requestId, action }: { requestId: string; action: 'accept' | 'decline' }) => {
      const { data } = await api.post(`/api/friends/requests/${requestId}/${action}`);
      return data;
    },
    onSuccess: () => invalidateFriendData(queryClient),
  });
};

export const useRemoveFriend = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (userId: string) => {
      await api.delete(`/api/friends/${userId}`);
    },
    onSuccess: () => invalidateFriendData(queryClient),
  });
};

export const useHeadToHead = (userId: string | undefined) =>
  useQuery({
    queryKey: queryKeys.headToHead(userId ?? ''),
    queryFn: async () => {
      const { data } = await api.get(`/api/friends/${userId}/head-to-head`);
      return data as HeadToHead;
    },
    enabled: !!userId,
  });

export const usePublicProfile = (userId: string | undefined) =>
  useQuery({
    queryKey: queryKeys.publicProfile(userId ?? ''),
    queryFn: async () => {
      const { data } = await api.get(`/db/users/${userId}/public`);
      return data.user as PublicProfile;
    },
    enabled: !!userId,
  });

// --- Challenges (Phase F) ---

export const useChallenges = () =>
  useQuery({
    queryKey: queryKeys.challenges,
    queryFn: async () => {
      const { data } = await api.get('/api/challenges');
      return (data.challenges ?? []) as ChallengeCard[];
    },
  });

export const useChallenge = (id: string | undefined) =>
  useQuery({
    queryKey: queryKeys.challenge(id ?? ''),
    queryFn: async () => {
      const { data } = await api.get(`/api/challenges/${id}`);
      return data.challenge as ChallengeCard;
    },
    enabled: !!id,
  });

interface CreateChallengeInput {
  opponentId: string;
  topic: string;
  difficulty: string;
  count: number;
}

export const useCreateChallenge = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ opponentId, ...body }: CreateChallengeInput) => {
      const { data } = await api.post('/api/challenges', { opponent_id: opponentId, ...body });
      return data.challenge as ChallengeCard;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['challenges'] }),
  });
};

/** Starts my run of a challenge — response shape matches useStartQuiz. */
export const usePlayChallenge = () =>
  useMutation({
    mutationFn: async (challengeId: string) => {
      const { data } = await api.post(`/api/challenges/${challengeId}/play`);
      return data as QuizSessionStart & { challenge_id: string };
    },
  });

export const useDeclineChallenge = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (challengeId: string) => {
      const { data } = await api.post(`/api/challenges/${challengeId}/decline`);
      return data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['challenges'] }),
  });
};

export const useRematch = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (challengeId: string) => {
      const { data } = await api.post(`/api/challenges/${challengeId}/rematch`);
      return data.challenge as ChallengeCard;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['challenges'] }),
  });
};

// --- Notifications (Phase F) ---

/**
 * The polling loop IS the push system until Phase J: one 30s interval,
 * paused while the tab is hidden. Everything social hangs off it.
 */
export const useNotifications = (enabled: boolean) =>
  useQuery({
    queryKey: queryKeys.notifications,
    queryFn: async () => {
      const { data } = await api.get('/api/notifications');
      return data as NotificationsResponse;
    },
    enabled,
    refetchInterval: 30_000,
    refetchIntervalInBackground: false,
  });

export const useMarkAllNotificationsRead = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      await api.post('/api/notifications/read-all');
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['notifications'] }),
  });
};

// --- Daily Challenge (Phase G) ---

export const useDaily = () =>
  useQuery({
    queryKey: ['daily'],
    queryFn: async () => {
      const { data } = await api.get('/api/daily');
      return data;
    },
  });

export const usePlayDaily = () =>
  useMutation({
    mutationFn: async () => {
      const { data } = await api.post('/api/daily/play');
      return data as QuizSessionStart;
    },
  });

export const useDailyLeaderboard = (day: string) =>
  useQuery({
    queryKey: ['daily-leaderboard', day],
    queryFn: async () => {
      const { data } = await api.get(`/api/daily/leaderboard?day=${day}`);
      return data;
    },
    enabled: !!day,
  });

export const useDailyCalendar = (month: string) =>
  useQuery({
    queryKey: ['daily-calendar', month],
    queryFn: async () => {
      const { data } = await api.get(`/api/daily/calendar?month=${month}`);
      return data;
    },
    enabled: !!month,
  });

// --- Wallet / Energy (Phase G) ---

export const useWallet = () =>
  useQuery({
    queryKey: ['wallet'],
    queryFn: async () => {
      const { data } = await api.get('/api/me/wallet');
      return data;
    },
  });
